import KeenSlider, { KeenSliderInstance } from 'keen-slider';

import {
  initCarousel,
  CarouselOptions,
  InitCarouselParams,
} from '../objects/carousel';
import {
  responsiveQuery,
} from '../settings/responsive';

const SELECTOR_CAROUSEL = '[data-social-reviews-carousel]';
const SELECTOR_MUTE_TOGGLE = '[data-social-reviews-video-mute-toggle]';
const SELECTOR_VIDEO_TOGGLE = '[data-social-reviews-video-pause-toggle]';
const SELECTOR_VIDEO = '[data-social-reviews-video]';
const SELECTOR_SLIDE = '[data-social-reviews-slide]';
const SELECTOR_MANUALLY_PAUSED = 'data-manually-paused';

const socialReviewsCarousel = class extends HTMLElement {
  carouselElement: HTMLElement | null;
  prefersReducedMotion: boolean;
  carousel: {
    slider: KeenSliderInstance | null,
    autoplay: () => void,
    slides: HTMLElement[],
  }
  
  constructor() {
    super();

    this.carouselElement = this.querySelector<HTMLElement>(SELECTOR_CAROUSEL);

    this.initSlider();
  }

  initSlider(): void {
    const options = {
      slides: {
        perView: 1.3,
        spacing: 15,
        origin: "center" as const,
      },      
      loop: true,
      breakpoints: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        [responsiveQuery('XSMALL')]: {
          slides: {
            perView: 'auto' as const,
            spacing: 20,
          },
          loop: true,
        },
      },
    };

    const params: InitCarouselParams = { 
      container: this.carouselElement, 
      options,
      onSlideChangedCallback: () => setTimeout(() => this.handleVideoPlayback(), 200),
    };
    this.carousel = initCarousel(params);
    
    setTimeout(() => this.handleVideoPlayback(), 100);

    this.initEventListeners();
  }  

  initEventListeners(): void {
    if (!this.carouselElement) return;
    const toggleMuteButtons = this.carouselElement.querySelectorAll<HTMLElement>(SELECTOR_MUTE_TOGGLE);
    const toggleVideoButtons = this.carouselElement.querySelectorAll<HTMLElement>(SELECTOR_VIDEO_TOGGLE);

    toggleMuteButtons.forEach((btn: HTMLButtonElement) => {
      btn.addEventListener('click', (e: MouseEvent) => {
        const slide: HTMLElement = (e.target as HTMLElement).closest(SELECTOR_SLIDE);
        this.handleVideoMuteToggle(slide);
      });
    });

    toggleVideoButtons.forEach((btn: HTMLButtonElement) => {
      btn.addEventListener('click', (e: MouseEvent) => {
        const slide: HTMLElement = (e.target as HTMLElement).closest(SELECTOR_SLIDE);

        this.handleVideoToggle(slide);
      });
    });
  }

  handleVideoPlayback(): void {
    if (!this.carousel?.slider) return;

    const slideDetails = this.carousel.slider.track.details.slides;
    const slides = this.carousel.slides;

    slides.forEach((slide, index) => {
      const video = slide.querySelector<HTMLVideoElement>('video');
      const toggleBtn = slide.querySelector<HTMLElement>(SELECTOR_VIDEO_TOGGLE);
      const manuallyPaused = slide.getAttribute(SELECTOR_MANUALLY_PAUSED) === 'true';
      if (!video) return;

      const slideInfo = slideDetails[index];
      const isVisible = slideInfo && slideInfo.portion > 0.3;

      if (isVisible) {
        if (video.paused && manuallyPaused !== true) {
          video.play().catch(() => {console.log('Video playback failed');});
          toggleBtn.setAttribute('aria-pressed', 'false');
        }
      } else {
        if (!video.paused) {
          video.pause();
          toggleBtn.setAttribute('aria-pressed', 'true');
        }
      }
    });
  }

  handleVideoMuteToggle(slide: HTMLElement): void {
    if (!slide) return;
    const video = slide.querySelector<HTMLVideoElement>(SELECTOR_VIDEO);
    const mutedBtn = slide.querySelector<HTMLElement>(SELECTOR_MUTE_TOGGLE);

    if (!video) return;

    video.muted = !video.muted;
    mutedBtn.setAttribute('aria-pressed', video.muted.toString());
  }

  handleVideoToggle(slide: HTMLElement): void {
    if (!slide) return;
    const video = slide.querySelector<HTMLVideoElement>(SELECTOR_VIDEO);
    const toggleBtn = slide.querySelector<HTMLElement>(SELECTOR_VIDEO_TOGGLE);

    if (!video) return;

    if (video.paused) {
      video.play().catch(() => {console.log('Video playback failed');});
      toggleBtn.setAttribute('aria-pressed', 'false');
      slide.removeAttribute(SELECTOR_MANUALLY_PAUSED);
    } else {
      video.pause();
      toggleBtn.setAttribute('aria-pressed', 'true');
      slide.setAttribute(SELECTOR_MANUALLY_PAUSED, 'true');
    }
  }
};

if (typeof window.customElements.get('social-reviews-carousel') === 'undefined') {
  window.customElements.define('social-reviews-carousel', socialReviewsCarousel);
}
